<template>
  <div
    v-if="showBanner"
    class="space flex cursor-pointer flex-row flex-wrap items-center justify-center gap-2 p-2"
    :style="{ backgroundColor: backgroundColor }"
    :data-ab-id="dataAbId"
    :aria-label="$t('top banner')"
    :tabindex="0"
  >
    <div
      class="flex min-w-0 max-w-full items-end whitespace-pre-line break-words"
      :style="{ color: textColor }"
      @click="handleClickRedirect"
      @enter="handleClickRedirect"
    >
      <span class="block min-w-0 max-w-full whitespace-pre-line break-words text-center">
        {{ info }}
      </span>
    </div>
    <KikLegalInformationDrawer
      v-if="enableLegalDisclaimer"
      :legal-text="legalText"
      :title="legalTitle"
      :color="textColor"
    />
    <KikButton
      v-if="discountCode !== ''"
      ref="discountButton"
      :style="{ backgroundColor: backgroundColorButton, color: textColorButton, borderColor: borderColorButton, borderWidth: '1px' }"
      class="w-auto min-w-0 max-w-full whitespace-pre-line break-words"
      @click.prevent="handleClickDiscount"
    >
      <span class="block min-w-0 max-w-full whitespace-pre-line break-words text-center">
        {{ discountCode }}
      </span>
    </KikButton>
  </div>
</template>

<script lang="ts" setup>
import type { includePagesStruct } from './Banner.model';
import { useMagnolia } from '~/composables/custom/useMagnolia/useMagnolia';
import useUiNotification from '~/composables/useUiNotification';

const { send } = useUiNotification();
const route = useRoute();
const { atMagnolia } = useMagnolia();
const { $domain } = useNuxtApp();

interface Props {
  promotionUrl: string;
  discountCode?: string;
  visible: boolean;
  info?: string;
  backgroundColor: string;
  textColor: string;
  backgroundColorButton: string;
  textColorButton: string;
  borderColorButton: string;
  dataAbId?: string;
  legalTitle?: string;
  legalText?: string;
  enableLegalDisclaimer?: boolean;
  showOnAllPages: boolean;
  showOnHomePage: boolean;
  showInPLP: boolean;
  showInPDP: boolean;
  showInCart: boolean;
  showInWishlist: boolean;
  showInAccountArea: boolean;
  showInAllContentPages: boolean;
  includePages: includePagesStruct[];
  excludePages: includePagesStruct[];
}

const {
  visible,
  legalTitle = '',
  legalText = '',
  enableLegalDisclaimer,
  discountCode = '',
  promotionUrl,
  info,
  textColor,
  textColorButton,
  borderColorButton,
  backgroundColorButton,
  backgroundColor,
  dataAbId = '',
  showOnAllPages,
  showInPLP,
  showInPDP,
  showInCart,
  showInWishlist,
  showInAccountArea,
  showInAllContentPages,
  includePages,
  excludePages,
  showOnHomePage,
} = defineProps<Props>();

const handleClickDiscount = async () => {
  try {
    await navigator.clipboard?.writeText(discountCode);
    send({
      message: 'The promotion code was copied',
      icon: 'check',
      type: 'success',
    });
  }
  catch {
    send({
      message: 'The promotion code could not be copied',
      icon: 'cross2',
      type: 'danger',
    });
  };
};

const discountButton = ref();

const handleClickRedirect = async () => {
  await navigateTo(promotionUrl, { redirectCode: 302, external: true });
};

const showBanner = computed(() => {
  const regex = /\/variants\/variant-\d+$/;

  if (!visible) {
    return false;
  }
  if (showOnAllPages) {
    return true;
  }
  const name = route.name;

  switch (name) {
    case 'category':
      return showInPLP;
    case 'product':
      return showInPDP;
    case 'Cart':
      return showInCart;
    case 'Wishlist':
      return showInWishlist;
    case 'my-account':
      return showInAccountArea;
    default:
      if (excludePages && excludePages.some(page => route.path.includes(page.pageLink.name))) {
        return false;
      }

      if (includePages && includePages.some(page => route.path.includes(page.pageLink.name))) {
        return true;
      }

      if (route.path === '/' || route.path.startsWith(`/${$domain.countryCode}/Home`) || (atMagnolia.value && route.path.startsWith(`/${$domain.countryCode}/Home`) && regex.test(route.fullPath))) {
        return showOnHomePage;
      }

      { return showInAllContentPages; }
  }
});
</script>
